<template>
  <BaseModal :modalTitle="$t('W: Tillægsydelser')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiComboboxMultiVee
        name="addonServiceIDs"
        :label="$t('W: Tillægsydelser')"
        :options="addonOptions"
        :emptyText="$t('W: Ingen tillægsydelser konfigureret')"
        v-model="selectedAddonIDs" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.save')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { useAddonServiceStore } from '@/stores/addonServiceStore'
import { useOrderStore } from '@/stores/orderStore'
import { useAppStore } from '@/stores/appStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions, mapState } from 'pinia'

export default {
  name: 'OrderAddonServicesModal',

  props: {
    orderID: { type: String as PropType<string>, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      selectedAddonIDs: [] as string[],
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAddonServiceStore, ['allAddonServices']),
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),

    order() {
      return this.allOrders.find((x) => x.id === this.orderID) ?? null
    },

    /** The order's per-order-scoped addon lines at the moment the modal opened —
     *  the diff base for onSubmit. Not reactive to further order changes while
     *  the modal is open; the form's own local selection is the source of truth
     *  for what the user is editing. */
    attachedAddonIDs(): string[] {
      return (this.order?.orderLines ?? [])
        .filter((line) => line.type === 'addon' && !line.analysisID)
        .map((line) => line.addonID ?? '')
    },

    addonOptions(): { value: string; text: string }[] {
      return this.allAddonServices
        .filter(
          (addonService) =>
            addonService.activeState === 'active' &&
            addonService.scope === 'per-order'
        )
        .map((addonService) => ({
          value: addonService.id,
          text: addonService.getName(this.currentLocale),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['addOrderAddonLine', 'removeOrderAddonLine']),
    ...mapActions(useModalStore, ['closeTop']),

    async onSubmit(): Promise<void> {
      const added = this.selectedAddonIDs.filter(
        (id) => !this.attachedAddonIDs.includes(id)
      )
      const removed = this.attachedAddonIDs.filter(
        (id) => !this.selectedAddonIDs.includes(id)
      )

      this.isLoading = true
      this.errorText = ''
      try {
        for (const addonID of added) {
          await this.addOrderAddonLine(this.orderID, addonID)
        }
        for (const addonID of removed) {
          await this.removeOrderAddonLine(this.orderID, addonID)
        }
        useToast().success(this.$t('W: Tillægsydelser opdateret'))
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.closeTop()
      } catch (err) {
        this.errorText =
          err instanceof Error ? err.message : this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke opdatere tillægsydelser'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.selectedAddonIDs = [...this.attachedAddonIDs]

    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    //
    // Registered after the pre-fill above, so the baseline is the order's
    // current state and only a real change reads as dirty.
    this.unsavedGuard.register({
      snapshot: () => ({ selectedAddonIDs: [...this.selectedAddonIDs] }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
